import * as Config from '../../config';
import Locale from '../../utils/Locale';
import * as UUID from '../../utils/Uuid';
import { AlertInfo, AlertType, AlertRecord, AlertCategories, CardConfig } from "../Types";
import { AlertLogic } from './AlertLogic';
import { getPumpInfo, getHeaterInfo, getSaltCellCatalogEntry } from '../../catalog';
import { ContentCategories } from "../../types";


export class NotResponding extends AlertLogic {
    static type = AlertType.NotResponding;
    static category = AlertCategories.EQUIPMENT_FAILURE;
    static contentCategories = [ ContentCategories.SYSTEM_ERROR ];
    static title = Locale.gettext('Not Responding');

    getInfo(timeZone?: string): AlertInfo {
        // Same for all alert types
        let { body, device, alertData, firstReported } = this.getCommons(timeZone);
        // Varying across alert types
        let detailIcon = 'alert';

        const lastSeen = alertData?.OfflineDateTime
            ? this.locale.gettext("Device last seen: %1$s",
                this.locale.formatDateTime(new Date(alertData?.OfflineDateTime), false, undefined, timeZone)
            )
            : ""
        ;

        const isOfflineToSecondary = alertData?.OfflineToSecondary === true;

        let kbUrl = '';
        if (isOfflineToSecondary) {
            kbUrl = 'https://poolside.support/contact-us/';
        } else {
            switch (device?.DeviceType) {
                case Config.DeviceTypes.PUMP: kbUrl = getPumpInfo(device)?.kbUrl; break;
                case Config.DeviceTypes.SALTCELL: {
                    if (device?.HardwareDevice?.PrimaryCatalogId?.length
                        && device?.HardwareDevice?.SecondaryCatalogId
                    ) {
                        kbUrl = getSaltCellCatalogEntry(
                            device?.HardwareDevice?.PrimaryCatalogId,
                            device?.HardwareDevice?.SecondaryCatalogId
                        )?.model?.kbUrl;
                    }
                    break;
                }
                case Config.DeviceTypes.HEATER:
                case Config.DeviceTypes.HEATPUMP:
                case Config.DeviceTypes.CHILLER:
                    kbUrl = getHeaterInfo(device)?.kbUrl;
                    break;
            }
        }

        const alertInfo = AlertInfo.fromCardConfig({
            cardTitle: this.locale.gettext('Device Not Responding'),
            cardDescription: isOfflineToSecondary ? this.locale.gettext('%1$s - secondary Attendant is offline', device?.Name) : this.locale.gettext('%1$s is not responding', device?.Name),
            cardIllustration: 'pipes_emergency',
            detailIcon: detailIcon,
            //detailTitle: this.locale.gettext('Device not responding'),
            detailDescriptionArr: isOfflineToSecondary ? [
                this.locale.gettext('Device "%1$s" %2$s is not responding because a secondary Attendant is offline.', device?.Name, device.HardwareDevice?.UniqueBusIdentifier ? '(Serial port: ' + device.HardwareDevice?.Ports?.[0]?.Number + ', ID: ' + device.HardwareDevice?.UniqueBusIdentifier + ')' : ''),
                this.locale.gettext('Please verify the secondary Attendant is powered on, and its communication cables to the primary are plugged in.'),
                this.locale.gettext('Contact your pool maintainer or Poolside Tech Support for additional assistance.'),
                lastSeen,
            ] : [
                this.locale.gettext('Device "%1$s" %2$s is not responding.', device?.Name, device.HardwareDevice?.UniqueBusIdentifier ? '(Serial port: ' + device.HardwareDevice?.Ports?.[0]?.Number + ', ID: ' + device.HardwareDevice?.UniqueBusIdentifier + ')' : ''),
                this.locale.gettext('Please check that it\'s powered on, the communication cables are correctly plugged in, and that you have configured the correct port settings in the configuration.'),
                this.locale.gettext('Contact your pool maintainer or the device manufacturer if the problem continues.'),
                lastSeen,
            ],
            actionButtonTitle: kbUrl ? this.locale.gettext('Knowledge Base') : '',
            actionButtonAction: kbUrl ? {
                actions: [
                    {
                        type: 'openUrl',
                        url: kbUrl
                    }
                ]
            } : undefined,
            actionButtonVisibleFor: undefined,
            actionButtonEnabledFor: undefined,
            resolveSummary: this.locale.gettext('%1$s is back online', device?.Name)
        });
        alertInfo.contentCategories = NotResponding.contentCategories;
        return alertInfo;
    }

    static mock(type:AlertType, config:Config.AllConfig): AlertRecord | null {
        const device = config.Config?.Devices?.find(dev => dev.DeviceType === "Pump");

        return device ? new AlertRecord({
            id: Math.floor(Math.random() * 100000),
            uuid: UUID.modifySection(4, UUID.modifySection(3, device.UUID, "MOCK"), "ALRT" + type),
            siteId: Math.floor(Math.random() * 100000),
            deviceId: 1,
            entityUuid: device?.UUID,
            bodyUuid: config.Config.BodiesOfWater?.[0]?.UUID,
            type: type,
            data: {
                OfflineDateTime: new Date(Date.now() - 60*60*1000),
            }
        }) : null;
    }
}